import React from 'react';
import ScorecardQuestionnaireStore from 'stores/scorecards/ScorecardQuestionnaireStore';
import ScorecardQuestionnaireMetadata from './ScorecardQuestionnaireMetadata';
import ScorecardQuestionnaireGrid from './ScorecardQuestionnaireGrid';
import FileUploadScorecardActions from 'actions/scorecards/FileUploadScorecardActions';
import ScorecardQuestionnaireActions from 'actions/scorecards/ScorecardQuestionnaireActions';
import './ScorecardQuestionnaire.styl';
import NavActions from 'actions/NavActions';
import {overrideName} from 'components/nav/renderers/ScorecardOverrideNav';
import Button from '@material-ui/core/Button';
import CheckIcon from 'material-ui/svg-icons/navigation/check';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import CoachingReviewStore from 'stores/coaching/CoachingReviewStore';
import {List} from 'immutable';
import CardReasonActions from 'actions/CardReasonActions';

/**
 * <ScorecardQuestionnaire /> renders a questionnaire for a `Scorecard` object
 */
export default class ScorecardQuestionnaire extends React.Component {
  static defaultProps = {
    topNav: overrideName,
  };

  constructor(props) {
    super(props);
    this.onCancel = this.onCancel.bind(this);
    this.submitAnswers = this.submitAnswers.bind(this);
    this.disableSubmit = this.disableSubmit.bind(this);
  }

  componentDidMount() {
    NavActions.setNavOverride.defer(this.props.topNav, {
      name: this.props.scorecard.name,
      submit: this.submitAnswers,
      cancel: this.onCancel,
      disableSubmit: this.disableSubmit,
      hasMultipleScorecards: this.props.hasMultipleScorecards,
      goToChooser: this.props.goToChooser,
      goBack: this.props.goBack,
    });
    CardReasonActions.fetchReasonSchemesForCall.defer(this.props.modelId);
    ScorecardQuestionnaireActions.beginEditing.defer();
  }

  componentWillUnmount() {
    NavActions.clearNavOverride.defer();
  }

  /**
   * Submit answers to all the questions for `this.props.scorecard`. Notice that for any questions the weren't
   * answered, we use the default value, 'n/a', as its answer
   */
  submitAnswers() {
    const {modelId, scorecard, onSubmit, taggedUsers} = this.props;

    ScorecardQuestionnaireStore.submitScorecardResults(
      modelId,
      this.props.sessionUser.id,
      scorecard,
      taggedUsers
    ).then(() => {
      FileUploadScorecardActions.clearAnswers(modelId, scorecard.id);
      onSubmit();
    });

    // Trigger a backend stats update if the scorecard is
    // part of a review.
    const {viewedScorecardsToReviews} = CoachingReviewStore.getState();
    const reviewId = viewedScorecardsToReviews.get(modelId);
    if (reviewId) {
      this.forceCoachingStatsUpdate(reviewId);
    }
  }

  async forceCoachingStatsUpdate(reviewId) {
    // Calling this endpoint, without any real updates, refreshes
    // stats for the review associated with this scorecard.
    await CoachingReviewActions.submitFeedback({id: reviewId}, List(), List());
  }

  /**
   * Check if all the questions with required reasons have the reasons given.
   */
  reasonIsRequired() {
    const {scorecard, reasonSchemes, reasons, answers} = this.props;

    // if no reasons yet, there can be no required reasons either
    if (!reasons) return false;

    const missingResults = scorecard.questions.map(question => {
      // there is no reason scheme defined for this question
      if (!question.cardReasonSchemeId) {
        return false;
      }

      const answer = answers ? answers.get(question.id) : undefined;

      // the question has no answer, so we don't require to have a reason set
      if (answer === undefined) {
        return false;
      }

      const scheme = reasonSchemes.find(scheme => scheme.id === question.cardReasonSchemeId);

      // we have no such scheme defined
      if (!scheme) {
        return false;
      }

      // we don't require the scheme to be mandatory
      if (scheme.optional) {
        return false;
      }

      // we may have chosen an answer (point) that does not have a reason scheme
      const reasonsForAnswer = scheme.entries.find(
        e => (e.point === null ? 'n/a' : String(e.point)) === answer
      );

      if (!reasonsForAnswer || !reasonsForAnswer.reasons || reasonsForAnswer.reasons.size === 0) {
        return false;
      }

      // at this point we have a question with an answer and a mandatory reason
      // so we need to have this set
      const reason = reasons && reasons.get(question.id);
      return reason === undefined;
    });

    return missingResults.some(i => i);
  }

  onCancel() {
    FileUploadScorecardActions.clearAnswers(this.props.modelId, this.props.scorecard.id);
    if (this.props.newCallCard) {
      this.props.goToChooser();
    } else {
      this.props.hasMultipleScorecards ? this.props.goToChooser() : this.props.goBack();
    }
  }

  disableSubmit() {
    return (
      this.props.isSaving ||
      !this.props.answers ||
      this.props.answers.count() === 0 ||
      this.reasonIsRequired()
    );
  }

  render() {
    const {scorecard, modelId, newCallCard} = this.props;

    const disableSubmit = this.disableSubmit();

    return (
      <div className="scorecard-questionnaire">
        <div className="button-row">
          {newCallCard && this.props.hasMultipleScorecards && (
            <Button onClick={this.onCancel}>Back to Scorecards</Button>
          )}
          {newCallCard && (
            <Button variant="outlined" onClick={this.props.goToResults}>
              View Report
            </Button>
          )}
        </div>

        <div>
          {!this.props.newCallCard && (
            <ScorecardQuestionnaireMetadata
              title={this.props.title}
              model={this.props.model}
              sessionUser={this.props.sessionUser}
              scorecard={scorecard}
              goToResults={this.props.goToResults}
            />
          )}
          <ScorecardQuestionnaireGrid
            newCallCard={this.props.newCallCard}
            scorecard={scorecard}
            answers={this.props.answers}
            comments={this.props.comments}
            reasons={this.props.reasons}
            labels={this.props.schemeLabels}
            onAddAnswer={(questionId, answer) => {
              ScorecardQuestionnaireActions.addAnswer(modelId, scorecard.id, questionId, answer);
            }}
            onRemoveAnswer={questionId => {
              ScorecardQuestionnaireActions.removeAnswer(modelId, scorecard.id, questionId);
              ScorecardQuestionnaireActions.addReason(modelId, scorecard.id, questionId);
            }}
            onAddComment={(questionId, comment, taggedUsers) =>
              ScorecardQuestionnaireActions.addComment(
                modelId,
                scorecard.id,
                questionId,
                comment,
                taggedUsers
              )
            }
            onRemoveComment={questionId =>
              ScorecardQuestionnaireActions.removeComment(modelId, scorecard.id, questionId)
            }
            onReasonChange={(questionId, reason) => {
              ScorecardQuestionnaireActions.addReason(modelId, scorecard.id, questionId, reason);
            }}
            sessionUser={this.props.sessionUser}
            reasonSchemes={this.props.reasonSchemes}
          />
        </div>
        <div className={'extra-wrapper'}>{this.props.children}</div>
        <div className="submit">
          <Button variant="outlined" className="ev-button" onClick={this.onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            className="ev-button"
            icon={<CheckIcon className="icon check" />}
            onClick={this.submitAnswers}
            disabled={disableSubmit}>
            Save
          </Button>
        </div>
      </div>
    );
  }
}
